import { createContext, lazy, Suspense, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useIsMobile } from '@worldcoo/ui/hooks/use-mobile';

const FilterPanelMobile = lazy(() => import('./filter-panel-mobile'));

type FilterPanelContextValue = {
	open: boolean;
	setOpen: (open: boolean) => void;
	toggle: () => void;
};

const FilterPanelContext = createContext<FilterPanelContextValue | null>(null);

export function useFilterPanel() {
	const ctx = useContext(FilterPanelContext);
	if (!ctx) throw new Error('useFilterPanel must be used within a FilterPanelProvider');
	return ctx;
}

export function FilterPanelProvider(props: { children: ReactNode }) {
	const [open, setOpen] = useState(false);
	const toggle = useCallback(() => setOpen((prev) => !prev), []);

	const value = useMemo(() => ({ open, setOpen, toggle }), [open, setOpen, toggle]);

	return <FilterPanelContext.Provider value={value}>{props.children}</FilterPanelContext.Provider>;
}

/** Lighter than the shadcn Sidebar: a width transition on desktop, a Sheet on mobile. */
export function FilterPanel(props: { children: ReactNode }) {
	const isMobile = useIsMobile();
	const { open, setOpen } = useFilterPanel();

	if (isMobile) {
		return (
			<Suspense>
				<FilterPanelMobile open={open} onOpenChange={setOpen}>
					{props.children}
				</FilterPanelMobile>
			</Suspense>
		);
	}

	return (
		<aside
			className="border-border sticky top-0 h-dvh self-start overflow-hidden border-l transition-[width] duration-200 ease-in-out"
			style={{ width: open ? '18rem' : '0px', borderLeftWidth: open ? undefined : '0px' }}
		>
			<div className="flex h-full w-72 flex-col">{props.children}</div>
		</aside>
	);
}
